import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, ArrowLeft, KeyRound, Copy, Check } from "lucide-react";
import { ToThaiLogo } from "@/components/ToThaiLogo";
import { toast } from "sonner";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resetLink, setResetLink] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/request-password-reset", { email: email.trim().toLowerCase() });
      setSubmitted(true);
      setResetLink(data?.reset_link || null);
    } catch (err) {
      setError(err.response?.data?.detail || "Nie udało się wygenerować linku resetu.");
    } finally {
      setLoading(false);
    }
  }

  function copyLink() {
    if (!resetLink) return;
    navigator.clipboard.writeText(resetLink).then(() => {
      setCopied(true);
      toast.success("Link skopiowany do schowka.");
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--tt-bg)] p-6">
      <Card className="tt-card w-full max-w-md" data-testid="forgot-password-page">
        <CardHeader>
          <div className="flex items-center justify-center mb-3">
            <ToThaiLogo layout="inline" size="sm" />
          </div>
          <CardTitle className="tt-display text-center">Resetuj hasło</CardTitle>
          <CardDescription className="text-center">
            Podaj e-mail powiązany z kontem. Wygenerujemy jednorazowy link (ważny 24h).
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!submitted ? (
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="reset-email">Email</Label>
                <Input
                  id="reset-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  placeholder="ty@tothai.local"
                  data-testid="forgot-password-email-input"
                />
              </div>
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <Button type="submit" className="w-full" disabled={loading || !email}
                      data-testid="forgot-password-submit-button">
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <KeyRound className="h-4 w-4 mr-2" />}
                Wyślij link resetu
              </Button>
            </form>
          ) : (
            <div className="space-y-4" data-testid="forgot-password-success">
              <Alert>
                <AlertDescription>
                  Jeśli adres istnieje w systemie, wygenerowaliśmy link resetu (ważny 24h).
                  Wysyłka e-mail jest tymczasowo wyłączona w trybie Beta — skorzystaj z linku poniżej.
                </AlertDescription>
              </Alert>
              {resetLink && (
                <div className="space-y-2">
                  <Label className="text-xs">Twój link resetu:</Label>
                  <div className="flex gap-2">
                    <Input value={resetLink} readOnly className="tt-mono text-xs" data-testid="forgot-password-link" />
                    <Button type="button" variant="outline" onClick={copyLink} data-testid="forgot-password-copy-button">
                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                  <Button type="button" className="w-full" onClick={() => navigate(new URL(resetLink).pathname)}>
                    Otwórz link teraz
                  </Button>
                </div>
              )}
            </div>
          )}
          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-[var(--tt-muted)] hover:text-[var(--tt-text)] inline-flex items-center gap-1"
                  data-testid="forgot-password-back-link">
              <ArrowLeft className="h-3.5 w-3.5" /> Wróć do logowania
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
